import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, Grid as AntGrid, Tag, Tooltip } from "antd";
import { Grid, type CellComponentProps } from "react-window";

import {
  getImagePlaceholder,
  normalizeImageUrl,
} from "../utils/imageUrlHelper.js";

export interface PosProduct {
  id_produit: number;
  item_type?: "product" | "service";
  service_id?: number | null;
  nom_produit: string;
  prix_vente: number;
  quantite_stock: number | null;
  // Recette vendable sans production : stock negatif assume (pas une rupture).
  sellable_without_production?: boolean;
  image: string;
  id_categorie: number;
  code_qr?: string;
  duration_minutes?: number | null;
}

type ItemData = {
  products: PosProduct[];
  columnCount: number;
  apiBaseUrl: string;
  onProductClick: (product: PosProduct) => void;
};

export type VirtualizedProductGridProps = {
  products: PosProduct[];
  apiBaseUrl: string;
  onProductClick: (product: PosProduct) => void;
  onEndReached?: () => void;
  bottomOffset?: number;
  layoutKey?: string | number;
};

const MIN_GRID_HEIGHT_PX = 240;
const DEFAULT_BOTTOM_OFFSET_PX = 24;
const CELL_PADDING_PX = 8;
const IMAGE_HEIGHT_PX = 150;
const ROW_HEIGHT_PX = 280;

function ProductCell({
  ariaAttributes,
  columnIndex,
  rowIndex,
  style,
  products,
  columnCount,
  apiBaseUrl,
  onProductClick,
}: CellComponentProps<ItemData>) {
  const index = rowIndex * columnCount + columnIndex;
  if (index >= products.length) {
    return <div style={style} {...ariaAttributes} />;
  }

  const product = products[index];
  const imageSrc = normalizeImageUrl(product.image, apiBaseUrl);

  return (
    <div
      {...ariaAttributes}
      style={{
        ...style,
        padding: CELL_PADDING_PX,
        boxSizing: "border-box",
      }}
    >
      <Card
        className="pos-product-card"
        hoverable
        onClick={() => onProductClick(product)}
        cover={
          <img
            src={imageSrc}
            alt={product.nom_produit}
            loading="lazy"
            decoding="async"
            style={{ height: IMAGE_HEIGHT_PX, objectFit: "cover" }}
            onError={(event) => {
              event.currentTarget.src = getImagePlaceholder(product.nom_produit);
            }}
          />
        }
        style={{ height: "100%" }}
      >
        <Card.Meta
          title={product.nom_produit}
          description={
            <div>
              {product.prix_vente.toLocaleString()} CFA
              <br />
              {product.item_type === "service" ? (
                <Tag color="blue" style={{ margin: 0 }}>
                  Service
                  {product.duration_minutes
                    ? ` · ${product.duration_minutes} min`
                    : ""}
                </Tag>
              ) : product.sellable_without_production ? (
                <Tooltip
                  title={`Vente sans production autorisée — stock actuel : ${product.quantite_stock} (régularisé en fin de journée)`}
                >
                  <Tag color="gold" style={{ margin: 0, cursor: "help" }}>
                    Sans prod.
                  </Tag>
                </Tooltip>
              ) : (
                product.quantite_stock == null ? "Stock à vérifier" : `Stock: ${product.quantite_stock}`
              )}
            </div>
          }
        />
      </Card>
    </div>
  );
}

const VirtualizedProductGrid: React.FC<VirtualizedProductGridProps> = ({
  products,
  apiBaseUrl,
  onProductClick,
  onEndReached,
  bottomOffset = DEFAULT_BOTTOM_OFFSET_PX,
  layoutKey,
}) => {
  const screens = AntGrid.useBreakpoint();

  const columnCount = useMemo(() => {
    if (screens.lg) return 6;
    if (screens.md) return 4;
    if (screens.sm) return 3;
    return 2;
  }, [screens.lg, screens.md, screens.sm]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [gridHeight, setGridHeight] = useState<number>(MIN_GRID_HEIGHT_PX);

  useEffect(() => {
    const updateHeight = () => {
      const element = containerRef.current;
      if (!element) return;

      const rect = element.getBoundingClientRect();
      const available = window.innerHeight - rect.top - bottomOffset;
      setGridHeight(Math.max(MIN_GRID_HEIGHT_PX, Math.floor(available)));
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [bottomOffset, layoutKey]);

  const itemData: ItemData = useMemo(
    () => ({
      products,
      columnCount,
      apiBaseUrl,
      onProductClick,
    }),
    [products, columnCount, apiBaseUrl, onProductClick]
  );

  const rowCount =
    columnCount > 0 ? Math.ceil(products.length / columnCount) : 0;

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      {gridHeight > 0 && rowCount > 0 ? (
        <Grid
          columnCount={columnCount}
          columnWidth={`${100 / columnCount}%`}
          rowCount={rowCount}
          rowHeight={ROW_HEIGHT_PX}
          cellComponent={ProductCell}
          cellProps={itemData}
          overscanCount={2}
          onCellsRendered={(visible) => {
            if (visible.rowStopIndex >= rowCount - 2) onEndReached?.();
          }}
          style={{ height: gridHeight, width: "100%" }}
        />
      ) : null}
    </div>
  );
};

export default VirtualizedProductGrid;
